import { el, clear, panel, empty, chip, button, table, row, ago, fmtMoney, toast } from '../ui.js';
import { get, ApiError } from '../api.js';

/**
 * Every call this household has had, and what each one produced.
 *
 * ---------------------------------------------------------------------------
 * THE GAP THIS CLOSES
 * ---------------------------------------------------------------------------
 *
 * Four finished, tested endpoints had no screen at all:
 *
 *   GET /v1/dial/:id/history    every legal move of one call, in order
 *   GET /v1/leads/:id/calls     every call placed to this household
 *   GET /v1/leads/:id/promises  every commitment made on those calls
 *   GET /v1/call-readings       what the reader heard, and who confirmed it
 *
 * The recording reference, the transcript, the state log, the disclosure and
 * the promises all existed server-side with nowhere to display them. For a
 * dialer whose whole premise is learning from what happened on a call, the one
 * screen that shows what happened on a call was the missing one.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT REFUSES TO DO
 * ---------------------------------------------------------------------------
 *
 * It does not play audio and it does not link to any. `recordingRef` is a
 * reference held by whoever stores the media — deliberately never a URL that
 * resolves without authentication — and turning it into a play button here
 * would be inventing a capability the server does not offer.
 *
 * It shows the transcript, which is already redacted: social security, routing,
 * account and card numbers are stripped before a word of it is stored, and a
 * row that still carries one is refused. What is left is a conversation about
 * somebody's family and money, so it sits behind the same permission as the
 * lead and is not summarised anywhere that leaves this screen.
 */

interface CallSession {
  id: string;
  state: string;
  dialedBy: string;
  dialedByKind: 'agent' | 'system';
  disposition: string | null;
  disclosureVersion: string | null;
  disclosedAt: string | null;
  transferToUserId: string | null;
  transferredAt: string | null;
  recordingRef: string | null;
  recordingRetainUntil: string | null;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  endedReason: string | null;
}

interface Transition {
  seq: number;
  toState: string;
  occurredAt: string;
  byUserId: string | null;
  detail: Record<string, unknown> | null;
}

interface Promise_ {
  id: string;
  kind: 'callback' | 'appointment';
  dueAt: string;
  state: string;
  ownerId: string;
  planText: string | null;
  settledAt: string | null;
}

interface Reading {
  id: string;
  status: string;
  createdAt: string;
  decidedBy: string | null;
  transcript: string;
  policyId: string | null;
  proposed: Extraction;
  accepted: Extraction | null;
}

interface Extraction {
  readBy?: string;
  autoOpened?: boolean;
  outcome: { value: string; quote: string };
  carrier: { value: string } | null;
  premium: { value: { cents: number; mode: string } } | null;
  faceAmountCents: { value: number } | null;
  lossReason?: string | null;
  callbackAt?: string | null;
  appointmentAt?: string | null;
  ambiguities: string[];
}

/**
 * A premium, always with its cents.
 *
 * `fmtMoney` in ui.ts rounds to whole dollars, which is right for a face amount
 * and wrong here: a premium of $47.30 rendered as "$47" is the number the
 * client agreed to and the number a commission is computed from, shown wrong on
 * the screen an agent checks the reading against. The first version of this
 * used `maximumFractionDigits` with no minimum and printed "$47.3", which is
 * the same mistake wearing a different coat.
 */
const premium = (cents: number): string =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const when = (iso: string | null): string =>
  (iso ? new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }) : '—');

export function callReviewView(leadId: string, go: (p: string) => void): HTMLElement {
  const host = el('div');
  const stage = el('div');

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Lead' }),
    el('h1', { text: 'Every call' }),
    el('p', { text: 'What was said, what it produced, and what was promised.' })));
  host.append(stage);

  const load = async (): Promise<void> => {
    clear(stage);
    stage.append(el('div', { class: 'note', text: 'Reading…' }));
    try {
      /*
       * All four at once. They are independent reads of the same household and
       * a screen that waits for each in turn is four round trips deep before
       * anything appears.
       *
       * `allSettled`, not `all`: the dialer routes 404 when no telephony is
       * configured at all, which is the state this agency is in today. One
       * absent section must not blank the other three — the transcripts are the
       * half that exists right now.
       */
      const [calls, promises, readings] = await Promise.allSettled([
        get<CallSession[]>(`/v1/leads/${encodeURIComponent(leadId)}/calls`),
        get<{ items: Promise_[] }>(`/v1/leads/${encodeURIComponent(leadId)}/promises`),
        // The per-lead route, not the pending queue with a query string on it:
        // `/v1/call-readings` returns what is waiting for confirmation across
        // the whole floor, which is a different question.
        get<{ items: Reading[] }>(`/v1/leads/${encodeURIComponent(leadId)}/call-readings`),
      ]);
      clear(stage);
      draw(stage, {
        calls: calls.status === 'fulfilled' ? calls.value : null,
        promises: promises.status === 'fulfilled' ? promises.value.items : null,
        readings: readings.status === 'fulfilled' ? readings.value.items : [],
      }, leadId, load, go);
    } catch (e) {
      clear(stage);
      const msg = e instanceof ApiError ? e.message : 'Could not read this.';
      stage.append(el('div', { class: 'warnbox' }, el('b', null, msg)));
      toast(msg, 'crit');
    }
  };
  void load();
  return host;
}

function draw(
  host: HTMLElement,
  data: { calls: CallSession[] | null; promises: Promise_[] | null; readings: Reading[] },
  leadId: string,
  reload: () => Promise<void>,
  go: (p: string) => void,
): void {
  const { calls, promises, readings } = data;

  if ((calls === null || calls.length === 0) && readings.length === 0) {
    host.append(panel('Nothing yet', empty(
      'No calls recorded for this household',
      'A call placed through the platform lands here on its own. A conversation from '
      + 'somewhere else arrives when a recording is transcribed or a transcript is pasted '
      + 'onto the lead.',
      button('Back to the lead', 'sm pri', () => go(`/leads/${leadId}`)),
    )));
    return;
  }

  /* ------------------------------------------------------- what was said -- */
  /*
   * One panel holding every reading, not one panel each.
   *
   * The first version repeated the heading "What the call said" above every
   * card, so a household with four conversations got the same title four times
   * and nothing said which was which. The date is on each entry; the heading
   * belongs to the section.
   */
  const said = el('div');
  for (const r of readings) {
    const x = r.accepted ?? r.proposed;
    const facts = el('div', { style: 'display:flex;gap:6px;flex-wrap:wrap;margin:6px 0' });
    facts.append(chip(r.status, r.status === 'accepted' ? 'good' : r.status === 'rejected' ? 'crit' : ''));
    /*
     * WHICH READER, ALWAYS ON SCREEN.
     *
     * A model reading and a pattern-matched one are different evidence, and the
     * agent looking at the card is the person best placed to notice when the
     * cheap one has guessed. Hiding it would also make the accuracy report —
     * which separates them — impossible to reconcile with what anybody saw.
     */
    facts.append(chip(x.readBy === 'model' ? 'read by model' : 'pattern reader', 'ir'));
    if (x.autoOpened) {
      /*
       * Said loudly, because nobody checked this one. The row names a decider
       * because the database requires one and because somebody owns the case —
       * but that person did not look at it, and a card that implies they did is
       * the one thing this feature must never do.
       */
      facts.append(chip('opened without review', 'warn'));
    }
    if (x.carrier) facts.append(chip(x.carrier.value, 'cop'));
    if (x.premium) facts.append(chip(`${premium(x.premium.value.cents)} ${x.premium.value.mode}`));
    if (x.faceAmountCents) facts.append(chip(`${fmtMoney(x.faceAmountCents.value)} cover`));
    if (x.lossReason) facts.append(chip(x.lossReason, 'crit'));
    if (x.callbackAt) facts.append(chip(`call back ${when(x.callbackAt)}`, 'warn'));
    if (x.appointmentAt) facts.append(chip(`appointment ${when(x.appointmentAt)}`, 'good'));

    const body = el('div');
    body.append(el('div', { class: 's', style: 'color:var(--ink-3);font-size:12px',
      text: `${when(r.createdAt)} · ${x.outcome.value.replace(/_/g, ' ')}` }));
    body.append(facts);
    if (x.outcome.quote) {
      body.append(el('blockquote', {
        style: 'margin:6px 0;padding:8px 12px;border-left:3px solid var(--copper);'
          + 'background:var(--hull-2);border-radius:4px;font-size:13px',
        text: `“${x.outcome.quote}”`,
      }));
    }
    for (const a of x.ambiguities) {
      body.append(el('div', {
        class: 's', style: 'font-size:12.5px;color:var(--warn);margin-top:3px', text: a,
      }));
    }
    if (r.policyId) {
      body.append(el('div', { class: 's', style: 'font-size:12.5px;margin-top:6px' },
        el('span', { text: 'A case was opened from this call. ' }),
        (() => {
          const b = button('Open the case', 'sm', () => go('/cases'));
          return b;
        })()));
    }

    /*
     * The transcript is collapsed by default. It is the most sensitive thing on
     * the screen and the least often needed — the extraction above exists
     * precisely so nobody has to read it — so it opens on a deliberate press
     * rather than sitting expanded in front of whoever walks past a desk.
     */
    const details = el('details', { style: 'margin-top:8px' });
    details.append(el('summary', {
      style: 'cursor:pointer;font-size:12.5px;color:var(--ink-3)',
      text: 'Show what was said',
    }));
    details.append(el('pre', {
      style: 'white-space:pre-wrap;font-size:12.5px;margin-top:6px;padding:10px;'
        + 'background:var(--hull-2);border-radius:6px;max-height:340px;overflow:auto',
      text: r.transcript,
    }));
    details.append(el('p', {
      class: 's', style: 'font-size:11.5px;color:var(--ink-3);margin-top:4px',
      text: 'Identifiers were removed before this was stored — no social security, bank, '
        + 'routing or card numbers. Everything else the client said is here.',
    }));
    body.append(details);

    body.style.cssText = 'padding:12px 0';
    if (said.childElementCount > 0) {
      body.style.cssText = 'padding:12px 0;border-top:1px solid var(--rim)';
    }
    said.append(body);
  }
  if (readings.length > 0) {
    host.append(panel(
      readings.length === 1 ? 'What the call said' : `What the calls said — ${readings.length}`,
      said,
    ));
  }

  /* --------------------------------------------------------- the promises -- */
  if (promises === null) {
    host.append(el('div', { class: 'note', text: 'Could not read the promises for this lead.' }));
  } else if (promises.length > 0) {
    host.append(panel(`Promised — ${promises.length}`, el('div', null,
      ...promises.map((p) => row({
        title: `${p.kind === 'appointment' ? 'Appointment' : 'Callback'} · ${when(p.dueAt)}`,
        sub: p.planText
          ? el('div', { class: 's', style: 'font-size:12.5px', text: `“${p.planText}”` })
          : undefined,
        right: chip(p.state, p.state === 'kept' ? 'good' : p.state === 'missed' ? 'crit' : 'warn'),
      })),
    ),
    el('p', { class: 's', style: 'color:var(--ink-3);font-size:12px;margin-top:8px',
      text: 'A promise carries a time, never just a date — writing down a date moves nothing '
        + 'and writing down a date and a time moves follow-through by about four points.' })));
  }

  /* ------------------------------------------------------------ the calls -- */
  if (calls === null) {
    host.append(el('div', { class: 'note' },
      el('b', null, 'No call records. '),
      el('span', {
        text: 'Telephony is not configured on this deployment, so nothing has been placed '
          + 'through the platform. Transcripts filed against the lead still appear above.',
      })));
  } else if (calls.length > 0) {
    host.append(panel(`Calls — ${calls.length}`, table(
      ['When', 'State', 'Outcome', 'Disclosure', 'Recording'],
      calls.map((s) => ({
        cells: [
          el('span', { text: when(s.startedAt) }),
          chip(s.state, s.state === 'completed' ? 'good' : s.state === 'failed' ? 'crit' : ''),
          el('span', { text: s.disposition ?? '—' }),
          /*
           * The disclosure is on this screen because it is the question an audit
           * actually asks: was it given, and was it given BEFORE the
           * conversation started. The state history below answers the second
           * half; this answers the first.
           */
          s.disclosedAt
            ? chip(s.disclosureVersion ?? 'given', 'good')
            : chip('none', 'crit'),
          /*
           * A reference, never a link. `recordingRef` is held by whoever stores
           * the media and is deliberately not a URL that resolves without
           * authentication — rendering it as a play button would invent a
           * capability the server does not offer.
           */
          s.recordingRef
            ? el('span', { class: 's', style: 'font-size:12px',
              text: s.recordingRetainUntil
                ? `held until ${when(s.recordingRetainUntil)}` : 'held' })
            : el('span', { class: 's', style: 'font-size:12px;color:var(--ink-3)', text: 'not recorded' }),
        ],
        onclick: () => void openHistory(s.id),
      })),
    ),
    el('p', { class: 's', style: 'color:var(--ink-3);font-size:12px;margin-top:8px',
      text: 'Open a call to see every move it made, in the order storage recorded them.' })));
  }

  const foot = el('div', { style: 'display:flex;gap:8px;margin-top:14px;flex-wrap:wrap' });
  foot.append(button('Back to the lead', 'sm', () => go(`/leads/${leadId}`)));
  foot.append(button('Refresh', 'sm', () => void reload()));
  host.append(foot);
}

/**
 * One call's state history, in a drawer.
 *
 * Ordered by `seq`, not by time, and that is the endpoint's own decision rather
 * than this screen's: several transitions land in the same millisecond — a
 * session opens and dials in one step — and a history whose order depends on
 * clock resolution is not evidence.
 */
async function openHistory(sessionId: string): Promise<void> {
  const { drawer } = await import('../ui.js');
  const body = el('div', { class: 'note', text: 'Reading…' });
  const close = drawer('Every move this call made', body);
  try {
    const rows = await get<Transition[]>(`/v1/dial/${encodeURIComponent(sessionId)}/history`);
    clear(body);
    body.classList.remove('note');
    if (rows.length === 0) {
      body.append(empty('No transitions recorded', 'This call has no history behind it.'));
      return;
    }
    body.append(table(['#', 'Moved to', 'When', 'By'], rows.map((t) => ({
      cells: [
        el('span', { class: 's', style: 'font-size:12px;color:var(--ink-3)', text: String(t.seq) }),
        chip(t.toState),
        el('span', { class: 's', style: 'font-size:12.5px', text: `${when(t.occurredAt)} · ${ago(t.occurredAt)}` }),
        el('span', { class: 's', style: 'font-size:12px', text: t.byUserId ?? 'the platform' }),
      ],
    }))));
  } catch (e) {
    clear(body);
    body.append(el('div', { class: 'warnbox' },
      el('b', null, e instanceof ApiError ? e.message : 'Could not read that call.')));
    void close;
  }
}
